import React, { useEffect, useState } from 'react';
import { Box, Card, CardContent, LinearProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Dashboard, Receipt, Description, Build } from '@mui/icons-material';
import { useAnalytics } from '../../api/analytics';
import { AnalyticsData } from '../../types/analytics';
import { formatCompletionPercent } from '../../utils';
import { Skeleton } from '@mui/material';
import { useNavigate } from 'react-router-dom';

export const StatCard: React.FC<{ title: string; value: React.ReactNode; icon: React.ReactNode; color?: string; onClick?: () => void; minWidth?: number }> = ({
  title,
  value,
  icon,
  color = '#1976d2',
  onClick,
  minWidth = 250,
}) => {
  const isSimpleValue = typeof value === 'string' || typeof value === 'number';

  return (
    <Card
      onClick={onClick}
      sx={{
        flex: `1 1 ${minWidth}px`,
        minWidth: `${minWidth}px`,
        borderRadius: 'var(--defaultRadius)',
        boxShadow: 3,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'transform 0.1s ease-in-out',
        '&:hover': {
          transform: onClick ? 'translateY(-2px)' : 'none'
        }
      }}
    >
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {title}
            </Typography>
            {isSimpleValue ? (
              <Typography variant="h4" component="div" fontWeight="bold">
                {value}
              </Typography>
            ) : (
              value
            )}
          </Box>
          <Box sx={{ color, fontSize: 48, opacity: 0.7 }}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export const DashboardComponent: React.FC = () => {
  const { t } = useTranslation();
  const { loading, getAnalytics } = useAnalytics();
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchAnalytics = async () => {
      const data = await getAnalytics();
      if (data) {
        setAnalyticsData(data);
      }
    };

    fetchAnalytics();
  }, [getAnalytics]);

  if (loading) {
    const StatCardSkeleton = ({ minWidth = 250 }: { minWidth?: number }) => (
      <Card sx={{ flex: `1 1 ${minWidth}px`, minWidth: `${minWidth}px`, borderRadius: 'var(--defaultRadius)', boxShadow: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box>
              <Skeleton variant="text" width={120} height={20} sx={{ mb: 1 }} />
              <Skeleton variant="text" width={80} height={36} />
            </Box>
            <Skeleton variant="circular" width={48} height={48} />
          </Box>
        </CardContent>
      </Card>
    );

    return (
      <Box p={3}>
        <Box display="flex" alignItems="center" mb={3}>
          <Skeleton variant="circular" width={32} height={32} sx={{ mr: 1 }} />
          <Skeleton variant="text" width={180} height={40} />
        </Box>

        {/* Jobs Section */}
        <Box mb={3}>
          <Skeleton variant="text" width={180} height={28} sx={{ mb: 1 }} />
          <Box display="flex" gap={2} flexWrap="wrap">
            <StatCardSkeleton minWidth={200} />
            <StatCardSkeleton minWidth={200} />
            <StatCardSkeleton minWidth={200} />
            <StatCardSkeleton minWidth={200} />
            <StatCardSkeleton minWidth={200} />
            <StatCardSkeleton minWidth={200} />
          </Box>
        </Box>

        {/* Settlements Section */}
        <Box mb={3}>
          <Skeleton variant="text" width={160} height={28} sx={{ mb: 1 }} />
          <Box display="flex" gap={2} flexWrap="wrap">
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
          </Box>
        </Box>

        {/* Contracts Section */}
        <Box mb={3}>
          <Skeleton variant="text" width={140} height={28} sx={{ mb: 1 }} />
          <Box display="flex" gap={2} flexWrap="wrap">
            <StatCardSkeleton />
            <StatCardSkeleton />
          </Box>
        </Box>
      </Box>
    );
  }

  if (!analyticsData) {
    return (
      <Box p={3}>
        <Typography variant="h6" color="text.secondary">
          {t('No analytics data available')}
        </Typography>
      </Box>
    );
  }

  const allCurrentMonth = analyticsData.settlements.all_current_month || 0;
  const readyToSettleCurrentMonth = analyticsData.settlements.ready_to_settle_current_month || 0;
  const confirmedCurrentMonth = analyticsData.settlements.confirmed_current_month || 0;
  const readyToSettleProgress =
    allCurrentMonth > 0 ? (readyToSettleCurrentMonth / allCurrentMonth) * 100 : 0;
  const readyToSettleProgressClamped = Math.max(0, Math.min(100, readyToSettleProgress));
  const settledProgress =
    readyToSettleCurrentMonth > 0 ? (confirmedCurrentMonth / readyToSettleCurrentMonth) * 100 : 0;
  const settledProgressClamped = Math.max(0, Math.min(100, settledProgress));

  const complaintsNotFinished = analyticsData.complaints_not_finished_count || 0;

  return (
    <Box p={3}>
      <Box display="flex" alignItems="center" mb={3}>
        <Dashboard sx={{ fontSize: 32, mr: 1 }} />
        <Typography variant="h4" component="h1">
          Dashboard
        </Typography>
      </Box>

      {/* Jobs Section */}
      <Box mb={3}>
        <Typography variant="h6" gutterBottom>
          {t('Service')}
        </Typography>
        <Box display="flex" flexWrap="wrap" gap={3}>
          <StatCard
            title={t('Pending')}
            value={analyticsData.jobs_by_status.P}
            icon={<Build />}
            color="#ed6c02"
            onClick={() => navigate('/service/jobs?status=P')}
            minWidth={200}
          />
          <StatCard
            title={t('In Progress')}
            value={analyticsData.jobs_by_status.IP}
            icon={<Build />}
            color="#9e9e9e"
            onClick={() => navigate('/service/jobs?status=IP')}
            minWidth={200}
          />
          <StatCard
            title={t('For Verification')}
            value={analyticsData.jobs_by_status.FV}
            icon={<Build />}
            color="#0288d1"
            onClick={() => navigate('/service/jobs?status=FV')}
            minWidth={200}
          />
          <StatCard
            title={t('For Improvement')}
            value={analyticsData.jobs_by_status.FI}
            icon={<Build />}
            color="#f57c00"
            onClick={() => navigate('/service/jobs?status=FI')}
            minWidth={200}
          />
          <StatCard
            title={t('Complaints')}
            value={complaintsNotFinished}
            icon={<Build />}
            color="#d32f2f"
            onClick={() => navigate('/service/jobs?kind=C')}
            minWidth={200}
          />
          <StatCard
            title={t('Finished')}
            value={analyticsData.jobs_by_status.F}
            icon={<Build />}
            color="#2e7d32"
            onClick={() => navigate('/service/jobs?status=F')}
            minWidth={200}
          />
        </Box>
      </Box>

      {/* Settlements Section */}
      <Box mb={3}>
        <Typography variant="h6" gutterBottom>
          {t('Settlements')}
        </Typography>
        <Box display="flex" gap={2} flexWrap="wrap">
          <StatCard
            title={t('Overdue Past Months')}
            value={analyticsData.settlements.overdue_past_months}
            icon={<Receipt />}
            color="#d32f2f"
            onClick={() => navigate('/settlements')}
          />
          <StatCard
            title={t('Ready to Settle / Current Month')}
            value={(
              <Box sx={{ minWidth: 170 }}>
                <Typography variant="h4" component="div" fontWeight="bold">
                  {`${readyToSettleCurrentMonth}/${allCurrentMonth}`}
                </Typography>
                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={readyToSettleProgressClamped}
                    sx={{ flex: 1, height: 8, borderRadius: 999 }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    {`${formatCompletionPercent(readyToSettleProgressClamped)}%`}
                  </Typography>
                </Box>
              </Box>
            )}
            icon={<Receipt />}
            color="#0288d1"
            onClick={() => navigate('/settlements')}
          />
          <StatCard
            title={t('Settled / Ready to Settle')}
            value={(
              <Box sx={{ minWidth: 170 }}>
                <Typography variant="h4" component="div" fontWeight="bold">
                  {`${confirmedCurrentMonth}/${readyToSettleCurrentMonth}`}
                </Typography>
                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={settledProgressClamped}
                    sx={{ flex: 1, height: 8, borderRadius: 999 }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    {`${formatCompletionPercent(settledProgressClamped)}%`}
                  </Typography>
                </Box>
              </Box>
            )}
            icon={<Receipt />}
            color="#2e7d32"
            onClick={() => navigate('/settlements')}
          />
        </Box>
      </Box>

      {/* Contracts Section */}
      <Box mb={3}>
        <Typography variant="h6" gutterBottom>
          {t('Contracts')}
        </Typography>
        <Box display="flex" gap={3} flexWrap="wrap">
          <StatCard
            title={t('Active')}
            value={analyticsData.contracts_active}
            icon={<Description />}
            color="#2e7d32"
            onClick={() => navigate('/contracts?ordering=end_date')}
          />
          <StatCard
            title={t('Expiring Soon')}
            value={analyticsData.contracts_expiring_soon}
            icon={<Description />}
            color="#ed6c02"
            onClick={() => navigate('/contracts?ordering=-end_date')}
          />
        </Box>
      </Box>
    </Box>
  );
};
